import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { startSetupTour, advanceSetupTour, readSetupTour, setSetupTourStep } from '../utils/setupOnboardingTours';
import { BILLING_BRAND } from '../data/billingBrandConfig';
import {
  trackTourStarted,
  trackTourStepViewed,
  trackTourStepCompleted,
  trackTourStuck,
  trackTourSkipped,
  trackTourCompleted,
} from '../utils/onboardingAnalytics';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  Info,
  Loader2,
  AlertCircle,
  LifeBuoy,
  Sparkles,
  X,
} from 'lucide-react';

const VIEWPORT_MARGIN = 16;
const TOOLTIP_GAP = 20;
const TOOLTIP_MAX_WIDTH = 340;
const TOOLTIP_MIN_WIDTH = 220;
const TOOLTIP_MIN_HEIGHT = 120;
const HIGHLIGHT_PADDING = 10;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Elementos que SON la accion de un paso (a diferencia de un contenedor que
// solo se resalta para explicar una pantalla).
const isClickableElement = (el) => (
  ['BUTTON', 'A', 'LABEL'].includes(el?.tagName) ||
  el?.getAttribute?.('role') === 'button' ||
  el?.getAttribute?.('role') === 'tab'
);

const uniquePlacements = (placement) => {
  const placements = [placement, 'bottom', 'right', 'left', 'top', 'center'].filter(Boolean);
  return placements.filter((item, index) => placements.indexOf(item) === index);
};

const getPlacementCoords = (targetRect, width, height, placement) => {
  const centerX = targetRect.left + (targetRect.width / 2) - (width / 2);
  const centerY = targetRect.top + (targetRect.height / 2) - (height / 2);

  switch (placement) {
    case 'top':
      return { left: centerX, top: targetRect.top - height - TOOLTIP_GAP };
    case 'right':
      return { left: targetRect.right + TOOLTIP_GAP, top: centerY };
    case 'left':
      return { left: targetRect.left - width - TOOLTIP_GAP, top: centerY };
    case 'center':
      return { left: centerX, top: centerY };
    case 'bottom-right':
      return { left: targetRect.right - width, top: targetRect.bottom + TOOLTIP_GAP };
    case 'bottom':
    default:
      return { left: centerX, top: targetRect.bottom + TOOLTIP_GAP };
  }
};

const getPlacementCapacity = (targetRect, placement, viewportWidth, viewportHeight) => {
  if (!targetRect || placement === 'center') {
    return {
      width: viewportWidth - (VIEWPORT_MARGIN * 2),
      height: viewportHeight - (VIEWPORT_MARGIN * 2),
    };
  }

  switch (placement) {
    case 'top':
      return {
        width: viewportWidth - (VIEWPORT_MARGIN * 2),
        height: Math.max(targetRect.top - TOOLTIP_GAP - VIEWPORT_MARGIN, 0),
      };
    case 'right':
      return {
        width: Math.max(viewportWidth - targetRect.right - TOOLTIP_GAP - VIEWPORT_MARGIN, 0),
        height: viewportHeight - (VIEWPORT_MARGIN * 2),
      };
    case 'left':
      return {
        width: Math.max(targetRect.left - TOOLTIP_GAP - VIEWPORT_MARGIN, 0),
        height: viewportHeight - (VIEWPORT_MARGIN * 2),
      };
    case 'bottom':
    default:
      return {
        width: viewportWidth - (VIEWPORT_MARGIN * 2),
        height: Math.max(viewportHeight - targetRect.bottom - TOOLTIP_GAP - VIEWPORT_MARGIN, 0),
      };
  }
};

// Un cartel que tapa el elemento que esta explicando no sirve: el usuario ve
// "hace clic aca" y no ve el "aca". Los clamp contra el borde de la pantalla
// pueden empujar el cartel de vuelta sobre el objetivo, asi que las posiciones
// candidatas se filtran por solapamiento antes de elegir.
const overlapsTarget = (candidate, targetRect) => {
  if (!targetRect) return false;
  const pad = 4;
  return (
    candidate.left < targetRect.right + pad &&
    candidate.left + candidate.width > targetRect.left - pad &&
    candidate.top < targetRect.bottom + pad &&
    candidate.top + candidate.height > targetRect.top - pad
  );
};

// Area que el cartel no deberia tapar. Es el objetivo mas el panel al que
// pertenece: cuando la guia resalta un item de un menu desplegado, tapar el
// resto del menu le saca al usuario justamente lo que tiene que aprender a
// ubicar. Se sube como maximo 4 niveles buscando el panel flotante (absolute /
// fixed); si no hay ninguno, no hay nada extra que evitar.
const unionRects = (a, b) => {
  if (!a) return b || null;
  if (!b) return a;
  const top = Math.min(a.top, b.top);
  const left = Math.min(a.left, b.left);
  const bottom = Math.max(a.bottom, b.bottom);
  const right = Math.max(a.right, b.right);
  return { top, left, right, bottom, width: right - left, height: bottom - top };
};

const getPanelAvoidRect = (element, targetRect) => {
  if (!element || typeof window === 'undefined') return targetRect || null;
  let node = element.parentElement;
  let depth = 0;
  while (node && node !== document.body && depth < 4) {
    const position = window.getComputedStyle(node).position;
    if (position === 'absolute' || position === 'fixed') {
      const rect = node.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return unionRects(rect, targetRect);
    }
    node = node.parentElement;
    depth += 1;
  }
  return targetRect || null;
};

const computeTooltipLayout = (targetRect, tooltipSize, preferredPlacement, avoidRect = null) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (!targetRect) {
    // Paso con ancla que aun no aparecio: el cartel espera arriba a la derecha,
    // por debajo de la barra de menu y a la altura del indicador de etapas de
    // la pantalla, sin taparle al usuario el area de trabajo.
    if (preferredPlacement === 'parked') {
      const width = Math.min(340, viewportWidth - (VIEWPORT_MARGIN * 2));
      const height = Math.min(tooltipSize.height || 300, viewportHeight - 160);
      return {
        width,
        height,
        left: viewportWidth > 900 ? viewportWidth - width - 24 : (viewportWidth - width) / 2,
        top: viewportWidth > 900 ? 132 : viewportHeight - height - 24,
        placement: 'parked',
      };
    }
    if (preferredPlacement === 'bottom-right') {
      const width = Math.min(360, viewportWidth - (VIEWPORT_MARGIN * 2));
      const height = Math.min(420, (tooltipSize.height || 340) + 12);
      const left = viewportWidth > 640
        ? viewportWidth - width - 24
        : (viewportWidth - width) / 2;
      const top = viewportWidth > 640
        ? 80
        : viewportHeight - height - 24;
      return {
        width,
        height,
        left,
        top,
        placement: 'bottom-right',
      };
    }
    const width = Math.min(520, viewportWidth - (VIEWPORT_MARGIN * 2));
    const height = Math.min(480, viewportHeight - (VIEWPORT_MARGIN * 2));
    return {
      width,
      height,
      left: (viewportWidth - width) / 2,
      top: (viewportHeight - height) / 2,
      placement: 'center',
    };
  }

  const isCentered = preferredPlacement === 'center';
  const maxWidth = isCentered ? 520 : TOOLTIP_MAX_WIDTH;
  const desiredWidth = Math.min(tooltipSize.width || maxWidth, viewportWidth - (VIEWPORT_MARGIN * 2));
  const desiredHeight = Math.min(
    tooltipSize.height || 280,
    viewportHeight - (VIEWPORT_MARGIN * 2),
  );

  const placements = uniquePlacements(preferredPlacement);
  const minimumWidth = Math.min(TOOLTIP_MIN_WIDTH, viewportWidth - (VIEWPORT_MARGIN * 2));
  const minimumHeight = Math.min(TOOLTIP_MIN_HEIGHT, viewportHeight - (VIEWPORT_MARGIN * 2));
  const candidates = placements
    .map((placement) => {
      const capacity = getPlacementCapacity(targetRect, placement, viewportWidth, viewportHeight);
      const width = Math.min(desiredWidth, capacity.width);
      const height = Math.min(desiredHeight, capacity.height);

      if (width <= 0 || height <= 0) return null;

      const coords = getPlacementCoords(targetRect, width, height, placement);

      return {
        placement,
        width,
        height,
        left: clamp(coords.left, VIEWPORT_MARGIN, viewportWidth - width - VIEWPORT_MARGIN),
        top: clamp(coords.top, VIEWPORT_MARGIN, viewportHeight - height - VIEWPORT_MARGIN),
        score: width * height,
      };
    })
    .filter(Boolean);

  // Primero se busca entre las posiciones que dejan libre el area a respetar
  // (el objetivo y, si lo hay, el panel que lo contiene); si ninguna sirve
  // (objetivo enorme o pantalla muy chica) se afloja a solo el objetivo y, por
  // ultimo, a todas.
  const blockRect = avoidRect || targetRect;
  const clear = candidates.filter((candidate) => !overlapsTarget(candidate, blockRect));
  const clearOfTarget = blockRect === targetRect
    ? clear
    : candidates.filter((candidate) => !overlapsTarget(candidate, targetRect));
  const pools = [clear, clearOfTarget, candidates].filter((pool) => pool.length);

  for (const pool of pools) {
    const perfectCandidate = pool.find((candidate) => (
      candidate.width >= desiredWidth &&
      candidate.height >= desiredHeight
    ));
    if (perfectCandidate) return perfectCandidate;

    const fittingCandidate = pool.find((candidate) => (
      candidate.width >= minimumWidth &&
      candidate.height >= minimumHeight
    ));
    if (fittingCandidate) return fittingCandidate;
  }

  const fallbackCandidate = (pools[0] || candidates)
    .slice()
    .sort((a, b) => b.score - a.score)[0];
  if (fallbackCandidate) {
    return fallbackCandidate;
  }

  return {
    width: desiredWidth,
    height: desiredHeight,
    left: clamp((viewportWidth - desiredWidth) / 2, VIEWPORT_MARGIN, viewportWidth - desiredWidth - VIEWPORT_MARGIN),
    top: clamp((viewportHeight - desiredHeight) / 2, VIEWPORT_MARGIN, viewportHeight - desiredHeight - VIEWPORT_MARGIN),
    placement: 'center',
  };
};

const getConnector = (targetRect, placement) => {
  if (!targetRect || placement === 'center') return null;

  const iconMap = {
    top: ArrowUp,
    bottom: ArrowDown,
    left: ArrowLeft,
    right: ArrowRight,
  };

  const Icon = iconMap[placement] || ArrowDown;
  const size = 36;

  switch (placement) {
    case 'top':
      return {
        Icon,
        left: clamp(targetRect.left + (targetRect.width / 2) - (size / 2), VIEWPORT_MARGIN, window.innerWidth - size - VIEWPORT_MARGIN),
        top: Math.max(targetRect.top - (size / 2), VIEWPORT_MARGIN),
      };
    case 'right':
      return {
        Icon,
        left: Math.min(targetRect.right + 6, window.innerWidth - size - VIEWPORT_MARGIN),
        top: clamp(targetRect.top + (targetRect.height / 2) - (size / 2), VIEWPORT_MARGIN, window.innerHeight - size - VIEWPORT_MARGIN),
      };
    case 'left':
      return {
        Icon,
        left: Math.max(targetRect.left - size - 6, VIEWPORT_MARGIN),
        top: clamp(targetRect.top + (targetRect.height / 2) - (size / 2), VIEWPORT_MARGIN, window.innerHeight - size - VIEWPORT_MARGIN),
      };
    case 'bottom':
    default:
      return {
        Icon,
        left: clamp(targetRect.left + (targetRect.width / 2) - (size / 2), VIEWPORT_MARGIN, window.innerWidth - size - VIEWPORT_MARGIN),
        top: Math.min(targetRect.bottom + 6, window.innerHeight - size - VIEWPORT_MARGIN),
      };
  }
};

// Deriva el nombre real del campo resaltado (lo que el usuario ve en pantalla),
// para mostrarlo en el cartel del spotlight en vez del titulo del paso. Solo
// aplica a campos de formulario; para botones/secciones devuelve null y el
// cartel cae al titulo del paso.
const getSpotlightFieldLabel = (el) => {
  if (!el) return null;
  const tag = el.tagName;
  if (tag !== 'INPUT' && tag !== 'SELECT' && tag !== 'TEXTAREA') return null;

  const clean = (text) => (text || '').replace(/\s+/g, ' ').trim();

  const aria = clean(el.getAttribute('aria-label'));
  if (aria) return aria;

  // <label> asociada formalmente (for/id o label envolvente)
  if (el.labels && el.labels.length) {
    const labelled = clean(el.labels[0].textContent);
    if (labelled) return labelled;
  }

  // Subir por los contenedores cercanos buscando la <label> mas proxima.
  // Se descartan las labels que envuelven su propio control (patron tipico del
  // boton "Cargar" con <input type="file"> oculto): esa label pertenece a otro
  // campo y hacia que el spotlight y el mensaje de validacion nombraran un
  // campo inexistente (decia "Cargar" cuando el campo era CUIT).
  let node = el.parentElement;
  for (let i = 0; i < 4 && node; i += 1) {
    const candidates = Array.from(node.querySelectorAll('label'));
    const own = candidates.find(
      (label) => label.contains(el) || !label.querySelector('input, select, textarea, button'),
    );
    if (own) {
      const text = clean(own.textContent);
      if (text) return text;
    }
    node = node.parentElement;
  }

  // No se usa el placeholder como nombre del campo: suele ser un valor de
  // ejemplo ("20123456789", "Mi Empresa S.A.") y termina en mensajes absurdos
  // del tipo 'Completá "20123456789" para continuar'. Sin etiqueta real es
  // mejor caer al titulo del paso, que esta escrito para el usuario.
  return null;
};

export default function ModuleOnboardingPrompt({
  moduleDefinition,
  saving = false,
  onComplete,
  onDismiss,
  dismissLabel = 'Mas tarde',
  completeLabel = 'Finalizar guia',
  nextLabel = 'Siguiente',
  introCompleteLabel = 'Entendido',
  onSkipStep,
  onExit,
  skipLabel = 'Saltear paso',
  exitLabel = 'Salir del recorrido',
}) {
  const tooltipRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, user } = useAuth();

  const [stepIndex, setStepIndex] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const tour = readSetupTour();
    const currentPath = window.location.pathname;
    const stepsList = Array.isArray(moduleDefinition?.steps) ? moduleDefinition.steps : [];
    if (tour && tour.stepId === moduleDefinition?.id && typeof tour.stepIndex === 'number') {
      let idx = tour.stepIndex;
      if (idx < stepsList.length && stepsList[idx]?.navStep) {
        const pageStepIdx = stepsList.findIndex((s) => s.selector && !s.navStep && ((s.route && currentPath.startsWith(s.route)) || (!s.route && moduleDefinition?.startRoute && currentPath.startsWith(moduleDefinition.startRoute))));
        if (pageStepIdx > idx) return pageStepIdx;
      }
      return idx;
    }
    // Sin recorrido guardado, el tour arranca por el paso 1. Antes esta rama
    // buscaba el primer paso con `selector` visible en la pantalla y arrancaba
    // ahi: como NINGUN paso 0 de los tours declara `selector` (son pantallas de
    // bienvenida, puro texto), el findIndex nunca podia devolver 0 y el
    // recorrido abria siempre en "Paso 2 de N", comiendose la introduccion.
    //
    // El catch-up hacia adelante lo resuelve el efecto de sincronizacion por
    // ruta, que corre cuando el usuario efectivamente navega. Acá no hace falta.
    return 0;
  });
  const [targetRect, setTargetRect] = useState(null);
  const [fieldLabel, setFieldLabel] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [tooltipLayout, setTooltipLayout] = useState({
    top: VIEWPORT_MARGIN,
    left: VIEWPORT_MARGIN,
    width: TOOLTIP_MAX_WIDTH,
    height: 320,
    placement: 'center',
  });

  const [validationError, setValidationError] = useState(null);
  const isGoingBackRef = useRef(false);
  // Solo se re-sincroniza hacia atras el desfasaje con el que arranca el
  // cartel (estado viejo restaurado de una sesion anterior). Una vez que el
  // usuario avanzo, su posicion manda: si no, al pasar a un paso cuya pantalla
  // todavia no abrio, el recorrido lo devolveria al paso anterior en loop.
  const allowBackHealRef = useRef(true);
  // ¿El usuario toco el elemento de ESTE paso? Es lo que habilita "Siguiente"
  // en los pasos cuya accion es un clic. Se reinicia al cambiar de paso.
  const stepInteractedRef = useRef(false);
  // Espejo en estado: la validacion usa el ref (sincrono), la UI usa el estado
  // para saber si "Siguiente" esta habilitado en este paso.
  const [stepInteracted, setStepInteracted] = useState(false);
  // Un paso avanza automaticamente UNA sola vez. Hay dos mecanismos que pueden
  // dispararse con la misma accion —el clic sobre el elemento y la aparicion
  // del elemento del paso siguiente— y al tocar "Configuración" corrian los
  // dos: el desplegable se abria (aparecia el item) y ademas el clic contaba,
  // asi que el recorrido saltaba dos pasos y el cartel desaparecia en una
  // pantalla que no era la suya.
  const autoAdvancedStepRef = useRef(-1);
  // Paso al que se esta hidratando el cartel al montarse. Mientras el estado
  // local todavia no llego ahi, no se puede persistir nada: seria escribir el 0
  // inicial de React encima del progreso guardado.
  const hydratingToRef = useRef(null);
  // handleComplete se define mas abajo; el listener de clic lo necesita para
  // cerrar el recorrido cuando el elemento del ultimo paso es el que se toca.
  const completeRef = useRef(null);
  // Un recorrido se cierra UNA sola vez. En el ultimo paso hay cuatro caminos
  // que pueden llamar a handleComplete con la MISMA accion del usuario: el
  // clic sobre el elemento resaltado, el MutationObserver de `doneWhen`, el
  // sondeo de layout (que ve desaparecer el selector del paso) y el boton
  // "Finalizar". Como ninguno sabia de los otros, guardar el ultimo registro
  // disparaba el festejo tres veces seguidas: tres toasts de "Mision de
  // aprendizaje completada", tres eventos trackTourCompleted y tres
  // onComplete(). Es el mismo problema que ya resuelve autoAdvancedStepRef
  // para los pasos intermedios, pero en el cierre.
  const tourCompletedRef = useRef(false);

  // Refs de telemetría y tiempos
  const stepStartTimeRef = useRef(Date.now());
  const tourStartTimeRef = useRef(Date.now());
  const validationAttemptsRef = useRef(0);

  const steps = Array.isArray(moduleDefinition?.steps) ? moduleDefinition.steps : [];
  const hasSteps = steps.length > 0;
  const currentStep = hasSteps ? (steps[stepIndex] || steps[0] || null) : null;

  // Aviso de ayuda humana. Se muestra donde el usuario decide si sigue solo
  // (el paso de bienvenida y los que ofrecen opciones), no en cada paso: ahi
  // seria ruido. El canal lo define la capa de marca (PROTECTED por app).
  const supportHelpHref = BILLING_BRAND?.supportHelpHref || '';
  const supportHelpLabel = BILLING_BRAND?.supportHelpLabel || 'Contactanos';
  const supportContactName = BILLING_BRAND?.supportContact || 'soporte';
  // ¿"Siguiente" puede avanzar este paso? Hay pasos cuya accion es tocar el
  // elemento resaltado (el menu, "+ Nuevo", guardar): ahi el boton no sirve y
  // antes tiraba un error rojo sin explicar por que. Se muestra deshabilitado,
  // con el motivo escrito.
  const manualNextBlocked = useMemo(() => {
    if (!currentStep?.selector) return false;
    if (currentStep?.optionalField || currentStep?.navStep) return false;
    if (stepIndex >= steps.length - 1) return false;
    if (typeof document === 'undefined') return false;
    if (currentStep.doneWhen) return !document.querySelector(currentStep.doneWhen);
    if (stepInteracted) return false;
    return false;
  }, [currentStep, stepIndex, steps.length, stepInteracted, targetRect]);

  // Un paso que avanza solo no ofrece "Siguiente": el boton invita a saltear
  // justamente la accion que la guia esta ensenando (abrir el menu, elegir la
  // tarjeta, guardar el producto). El paso se cierra cuando la accion se hizo
  // de verdad: por el clic en el elemento resaltado o por la prueba en el DOM.
  const selfAdvancing = Boolean(currentStep?.selector)
    && (Boolean(currentStep?.navStep) || Boolean(currentStep?.doneWhen))
    && currentStep?.autoAdvance !== false;

  // Red de seguridad: si el auto-avance no llego en unos segundos (una pantalla
  // que no cargo, un selector que cambio), el boton aparece para que nadie
  // quede encerrado en la guia.
  const [showManualEscape, setShowManualEscape] = useState(false);
  useEffect(() => {
    setShowManualEscape(false);
    if (!selfAdvancing) return undefined;
    const timer = setTimeout(() => setShowManualEscape(true), 12000);
    return () => clearTimeout(timer);
  }, [stepIndex, selfAdvancing]);
  // `hideAdvance`: el paso se cierra por otra via (una navegacion que el propio
  // recorrido detecta) y ofrecer "Siguiente" solo invita a saltear la accion que
  // se esta ensenando. No deja a nadie encerrado: el cartel conserva la X y
  // "Pausar guia".
  const showAdvanceButton = currentStep?.hideAdvance
    ? false
    : (!selfAdvancing || showManualEscape);

  const showSupportNote = Boolean(currentStep) && (stepIndex === 0 || Boolean(currentStep?.options));
  const isNavStep = String(moduleDefinition?.id || '').startsWith('nav-to-');

  // Red de seguridad: el cartel de felicitación se apaga solo. Si algún camino
  // deja de llamar al apagado explícito, el velo no puede quedar bloqueando la
  // pantalla de forma indefinida.
  useEffect(() => {
    if (!showConfetti) return undefined;
    const timer = setTimeout(() => setShowConfetti(false), 2500);
    return () => clearTimeout(timer);
  }, [showConfetti]);

  // Registrar inicio de tour y resetear marcas de tiempo al cambiar de modulo
  useEffect(() => {
    const tour = readSetupTour();
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    let targetIdx = 0;

    if (tour && tour.stepId === moduleDefinition?.id && typeof tour.stepIndex === 'number') {
      targetIdx = tour.stepIndex;
      // Si el tour está trabado en un navStep pero el usuario ya está en una pantalla con selectores del tour
      if (targetIdx < steps.length && steps[targetIdx]?.navStep) {
        const pageStepIdx = steps.findIndex((s) => s.selector && !s.navStep && ((s.route && currentPath.startsWith(s.route)) || (!s.route && moduleDefinition?.startRoute && currentPath.startsWith(moduleDefinition.startRoute))));
        if (pageStepIdx > targetIdx) {
          targetIdx = pageStepIdx;
          setSetupTourStep(targetIdx);
        }
      }
    }
    // Sin recorrido guardado, targetIdx queda en 0 a proposito: un tour que
    // recien arranca empieza por su pantalla de bienvenida.
    //
    // Aca habia una tercera copia del mismo salto (la otra estaba en el estado
    // inicial de stepIndex y en el efecto de sync por ruta): buscaba el primer
    // paso con `selector` visible y arrancaba ahi. Como ningun paso 0 declara
    // `selector` —son de texto—, el findIndex jamas devolvia 0 y el recorrido
    // abria en "Paso 2 de N". Alcanzar al usuario que ya navego es trabajo del
    // efecto de sincronizacion por ruta, no del arranque.

    // Se anota a donde vamos ANTES de pedir el cambio de estado: el efecto de
    // persistencia corre en este mismo commit, cuando `stepIndex` todavia vale
    // el 0 inicial, y sin esta marca guardaba ese 0 y perdia el recorrido.
    hydratingToRef.current = targetIdx;
    setStepIndex(targetIdx);
    setValidationError(null);
    // El cartel de "misión completada" pertenece al tour anterior: si el
    // componente no se desmonta (el recorrido sigue con otro módulo) y no lo
    // apagamos acá, queda tapando la pantalla con el blur para siempre.
    setShowConfetti(false);
    tourStartTimeRef.current = Date.now();
    stepStartTimeRef.current = Date.now();
    validationAttemptsRef.current = 0;

    if (moduleDefinition?.id) {
      trackTourStarted({
        tourId: moduleDefinition.id,
        organizationId: profile?.organization_id,
        userId: profile?.id || user?.id,
        stepCount: steps.length,
      });
    }
  }, [moduleDefinition?.id, moduleDefinition?.startRoute, profile?.organization_id, profile?.id, user?.id, steps]);

  // Este efecto es un catch-up: alcanza al usuario cuando NAVEGA. Sin esta
  // marca tambien corria en el montaje, y como el paso 0 no tiene `selector`
  // pero el 1 si (y ya estaba en pantalla), empujaba el recorrido a "Paso 2 de
  // N" apenas se abria el cartel.
  const rutaInicialRef = useRef(null);
  const huboNavegacionRef = useRef(false);
  useEffect(() => {
    if (rutaInicialRef.current === null) {
      rutaInicialRef.current = location.pathname;
      return;
    }
    if (location.pathname !== rutaInicialRef.current) huboNavegacionRef.current = true;
  }, [location.pathname]);

  // Sincronizar automáticamente el paso cuando el usuario navega a la pantalla destino de un paso
  useEffect(() => {
    if (!steps || steps.length === 0) return;

    // Arranque del recorrido: la bienvenida no se saltea. Un recorrido retomado
    // entra con stepIndex > 0 y no pasa por acá, asi que el resume sigue igual.
    if (stepIndex === 0 && !huboNavegacionRef.current) return;

    const currentPath = location.pathname;

    // Si estamos en un navStep o en el inicio y el usuario ya está en la pantalla del módulo
    if (steps[stepIndex]?.navStep || (stepIndex === 0 && currentPath !== '/sistema/progreso' && currentPath !== '/sistema/progreso-onboarding')) {
      const activeStepOnPage = steps.findIndex((s, idx) => {
        if (idx === 0 || s.navStep) return false;
        if (s.route && (currentPath === s.route || currentPath.startsWith(`${s.route}/`))) return true;
        if (s.selector && typeof document !== 'undefined' && document.querySelector(s.selector)) return true;
        return false;
      });

      // Solo hacia adelante: este efecto existe para que el recorrido alcance
      // la pantalla en la que ya esta el usuario, nunca para devolverlo atras.
      if (activeStepOnPage > stepIndex) {
        hydratingToRef.current = activeStepOnPage;
        setStepIndex(activeStepOnPage);
        setSetupTourStep(activeStepOnPage);
        setValidationError(null);
        return;
      }
    }

    // Si la ruta actual coincide con la ruta declarada de un paso posterior, sincronizar a ese paso
    const matchingStepIdx = steps.findIndex((s, idx) => {
      if (idx <= stepIndex) return false;
      return s.route && (currentPath === s.route || currentPath.startsWith(`${s.route}/`));
    });

    if (matchingStepIdx >= 0 && matchingStepIdx !== stepIndex) {
      // El salto no puede pasar por encima de un paso que pertenece a la
      // pantalla en la que el usuario ya esta parado. Los tours intercalan
      // pasos con `route` y pasos que solo traen `selector` (los campos de un
      // formulario, por ejemplo): si el paso 3 no declaraba ruta y el 4 si,
      // navegar saltaba del 2 al 4 y el contador mostraba "Paso 2 de 5" y
      // despues "Paso 4 de 5", sin el 3.
      //
      // Los navStep ("abri este menu") si se saltean a proposito: si el usuario
      // ya navego, pedirle que abra el menu no tiene sentido.
      const esDeLaPantallaActual = (paso) => {
        if (!paso || paso.navStep || paso.route) return false;
        if (!paso.selector) return true;
        if (typeof document === 'undefined') return false;
        return Boolean(document.querySelector(paso.selector));
      };

      let destino = matchingStepIdx;
      for (let i = stepIndex + 1; i < matchingStepIdx; i += 1) {
        if (esDeLaPantallaActual(steps[i])) {
          destino = i;
          break;
        }
      }

      hydratingToRef.current = destino;
      setStepIndex(destino);
      setSetupTourStep(destino);
      setValidationError(null);
    }
  }, [location.pathname, steps, stepIndex]);

  // Telemetria al cambiar de paso: registrar vista y paso completado anterior
  useEffect(() => {
    setValidationError(null);
    if (!currentStep) return;

    const now = Date.now();
    const timeSpentMs = now - stepStartTimeRef.current;

    if (stepIndex > 0) {
      trackTourStepCompleted({
        tourId: moduleDefinition?.id || 'tour',
        stepIndex: stepIndex - 1,
        timeSpentMs,
        organizationId: profile?.organization_id,
        userId: profile?.id || user?.id,
      });
    }

    stepStartTimeRef.current = now;
    validationAttemptsRef.current = 0;
    // Cada paso arranca sin interaccion registrada: lo que hiciste en el paso
    // anterior no habilita el siguiente.
    stepInteractedRef.current = false;
    setStepInteracted(false);
    autoAdvancedStepRef.current = -1;

    trackTourStepViewed({
      tourId: moduleDefinition?.id || 'tour',
      stepIndex,
      stepTitle: currentStep.title || '',
      selector: currentStep.selector || '',
      organizationId: profile?.organization_id,
      userId: profile?.id || user?.id,
    });
  }, [stepIndex, currentStep, moduleDefinition?.id, profile?.organization_id, profile?.id, user?.id]);

  useEffect(() => {
    const syncTourStep = () => {
      const tour = readSetupTour();
      if (tour && tour.stepId === moduleDefinition?.id && typeof tour.stepIndex === 'number') {
        setStepIndex(tour.stepIndex);
      }
    };
    const handleExternalAdvance = () => {
      setStepIndex((idx) => Math.min(idx + 1, steps.length - 1));
    };
    const handleExternalPrev = () => {
      setStepIndex((idx) => Math.max(idx - 1, 0));
    };
    const handleExternalSet = (e) => {
      if (typeof e.detail === 'number') {
        setStepIndex(clamp(e.detail, 0, steps.length - 1));
      }
    };

    window.addEventListener('impulza:setup-tour-change', syncTourStep);
    window.addEventListener('impulza:setup-tour-advance', handleExternalAdvance);
    window.addEventListener('impulza:setup-tour-prev', handleExternalPrev);
    window.addEventListener('impulza:setup-tour-set-step', handleExternalSet);

    return () => {
      window.removeEventListener('impulza:setup-tour-change', syncTourStep);
      window.removeEventListener('impulza:setup-tour-advance', handleExternalAdvance);
      window.removeEventListener('impulza:setup-tour-prev', handleExternalPrev);
      window.removeEventListener('impulza:setup-tour-set-step', handleExternalSet);
    };
  }, [moduleDefinition?.id, steps]);

  // Un paso se aprueba por lo que el usuario hizo EN ESE PASO. Antes el freno
  // miraba si el elemento del paso SIGUIENTE ya estaba en pantalla, que no dice
  // nada del paso actual: si el siguiente vivia en la misma pantalla pasaba
  // cualquiera, y si vivia en otra no pasaba nadie.
  //
  // Criterio, en orden:
  //   1. `doneWhen`: el paso declara la prueba de que se hizo (por ejemplo,
  //      "existe la pantalla de mapeo" => la planilla se cargo).
  //   2. Paso sin ancla (bienvenidas, cierres): no hay nada que exigir.
  //   3. Campo (input/select/textarea): tiene que tener un valor cargado.
  //   4. Boton, enlace o label: hay que haberlo tocado de verdad.
  //   5. Contenedor resaltado (una pantalla entera): es informativo, pasa.
  const runValidation = useCallback((options = {}) => {
    // allowMissingTarget: el avance automatico se dispara justo cuando la
    // pantalla cambio, o sea cuando el ancla del paso acaba de desmontarse.
    // Exigirle que siga en el DOM lo bloquearia siempre.
    const { allowMissingTarget = false } = options || {};

    // El ultimo paso siempre se puede cerrar: si no, un tour que termina sobre
    // un boton queda pidiendo para siempre un clic que ya no lleva a ningun lado.
    if (stepIndex >= steps.length - 1) return true;
    if (!currentStep?.selector) return true;

    const fail = (errorMsg) => {
      setValidationError(errorMsg);
      validationAttemptsRef.current += 1;
      if (validationAttemptsRef.current >= 2) {
        trackTourStuck({
          tourId: moduleDefinition?.id || 'tour',
          stepIndex,
          stepTitle: currentStep?.title || '',
          reason: errorMsg,
          attemptsCount: validationAttemptsRef.current,
          organizationId: profile?.organization_id,
          userId: profile?.id || user?.id,
        });
      }
      return false;
    };

    const stepName = currentStep?.title || 'este paso';
    // Cada paso puede escribir su propio aviso: "todavia no completaste X" no
    // le dice a nadie que boton tocar. `doneHint` es esa instruccion concreta.
    const hint = currentStep?.doneHint;

    // 1. Manda sobre todo lo demas, tambien sobre el avance automatico: si el
    //    paso declara como se prueba que se hizo, ni un clic en el elemento
    //    resaltado alcanza. (Tocar "Seleccionar Excel" abre el explorador; que
    //    lo hayas abierto no significa que hayas elegido un archivo.)
    if (currentStep.doneWhen) {
      if (document.querySelector(currentStep.doneWhen)) return true;
      return fail(hint || `Todavía no completaste "${stepName}". Hacelo en la pantalla y el recorrido sigue solo.`);
    }

    const el = document.querySelector(currentStep.selector);

    // 2 (el ancla se fue de pantalla o el elemento no existe)
    if (!el) {
      return true;
    }

    // 3. Un paso puede marcar su campo como opcional (marca, codigo de barras,
    //    stock minimo): se explica, pero no bloquea si el usuario lo deja vacio.
    if (['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName) && !currentStep.optionalField) {
      if (el.tagName === 'SELECT') {
        const realOptions = Array.from(el.options || []).filter((o) => o.value !== '');
        if (realOptions.length === 0) {
          return true;
        }
      }
      const val = el.value ? el.value.trim() : '';
      const label = getSpotlightFieldLabel(el) || stepName;
      if (!val || val === '0' || (el.type === 'number' && Number(val) <= 0)) {
        return fail(hint || `Completá "${label}" para continuar.`);
      }
      return true;
    }

    // 4. El paso apunta a un boton. Antes, si el usuario avanzaba a mano, la
    //    validacion le hacia el clic "por las dudas". Eso disparaba acciones
    //    reales sin que nadie las pidiera: tocar "Finalizar guia" en el ultimo
    //    paso del POS ejecutaba la venta, y el reset posterior borraba el medio
    //    de pago y el carrito. Avanzar la guia no puede operar el negocio.
    if (isClickableElement(el)) {
      return true;
    }

    // 5
    return true;
  }, [currentStep, steps, stepIndex, moduleDefinition?.id, profile?.organization_id, profile?.id, user?.id]);

  const handleNext = useCallback((options = {}) => {
    const { autoAdvanced = false } = options || {};
    setValidationError(null);
    if (autoAdvanced && autoAdvancedStepRef.current === stepIndex) return;
    if (!runValidation({ allowMissingTarget: autoAdvanced })) {
      // Un intento automatico que no pasa la validacion no es un error del
      // usuario: solo significa que la pantalla siguiente todavia no esta.
      // Mostrar el cartel rojo ahi (por ejemplo al tocar "Nuevo proveedor",
      // que navega) asustaba sin motivo, y el paso avanza solo cuando el
      // ancla aparece.
      if (autoAdvanced) setValidationError(null);
      return;
    }
    if (autoAdvanced) autoAdvancedStepRef.current = stepIndex;
    allowBackHealRef.current = false;
    const nextIdx = Math.min(steps.length - 1, stepIndex + 1);
    setStepIndex(nextIdx);
    setSetupTourStep(nextIdx);
    const nextStep = steps[nextIdx];
    if (nextStep?.route && typeof window !== 'undefined' && window.location.pathname !== nextStep.route && !window.location.pathname.startsWith(nextStep.route + '/')) {
      navigate(nextStep.route);
    }
  }, [runValidation, steps, stepIndex, navigate]);

  // Auto-avance por aparicion del paso siguiente.
  //
  // Hay pasos cuya accion no es un clic ni un input que se pueda escuchar: en
  // "Seleccionar tu planilla" el usuario abre el explorador de archivos y la
  // pantalla de mapeo recien aparece cuando el Excel termino de parsearse. El
  // clic en el boton no sirve como senal (puede cancelar el dialogo) y el
  // input real esta oculto dentro del label.
  //
  // La senal confiable es el DOM: si al entrar al paso el ancla del siguiente
  // NO existia y despues aparece, es que el usuario hizo lo que habia que
  // hacer. Solo se dispara en esa transicion ausente -> presente, asi que en
  // los tours donde todos los campos conviven en un mismo formulario no salta.
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return undefined;
    const nextSelector = currentStep?.doneWhen;
    if (!nextSelector) return undefined;
    if (document.querySelector(nextSelector)) return undefined;

    let done = false;
    const observer = new MutationObserver(() => {
      if (done || !document.querySelector(nextSelector)) return;
      done = true;
      observer.disconnect();
      // Un respiro para que la pantalla nueva termine de montarse antes de
      // reposicionar el cartel sobre ella. En el ultimo paso no hay "siguiente":
      // la prueba de completado ES el final del recorrido.
      setTimeout(() => {
        if (stepIndex >= steps.length - 1) completeRef.current?.();
        else handleNext({ autoAdvanced: true });
      }, 250);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [steps, stepIndex, handleNext]);

  // El cartel se desmonta cuando el usuario cambia de pantalla (cada guia se
  // renderiza solo donde viven sus selectores) y al volver nacia de nuevo en el
  // paso 1: el avance vivia solo en el estado local de React y nunca llegaba al
  // motor. Ahora cada cambio de paso se persiste, asi navegar por el menu, tocar
  // "+ Nuevo" o guardar el producto no reinicia el recorrido.
  useEffect(() => {
    const tour = readSetupTour();
    if (!tour || tour.stepId !== moduleDefinition?.id) return;

    // Ya estan sincronizados: la hidratacion termino.
    if (tour.stepIndex === stepIndex) {
      hydratingToRef.current = null;
      return;
    }

    // Hidratacion en curso: el estado local todavia no llego al paso guardado.
    if (hydratingToRef.current !== null && stepIndex !== hydratingToRef.current) return;

    // Invariante: la persistencia nunca rebobina un recorrido al paso 1. Volver
    // al principio es algo que solo hace "Repetir" (startSetupTour, que escribe
    // el estado el mismo). Cualquier 0 que llegue hasta aca es el valor inicial
    // de React en un cartel que se acaba de montar tras un cambio de ruta:
    // guardarlo era exactamente el reinicio que se veia al navegar.
    if (stepIndex < tour.stepIndex) return;

    hydratingToRef.current = null;
    setSetupTourStep(stepIndex);
  }, [stepIndex, moduleDefinition?.id]);

  // Escuchadores de eventos para Auto-Advance en tiempo real
  useEffect(() => {
    if (!currentStep?.selector) return undefined;
    const targetEl = document.querySelector(currentStep.selector);
    if (!targetEl) return undefined;

    const tagName = targetEl.tagName;
    const isInput = ['INPUT', 'SELECT', 'TEXTAREA'].includes(tagName);
    // Solo cuentan como accion del paso los elementos que SON la accion. Cuando
    // el ancla es un contenedor (la pantalla de mapeo, un listado), cualquier
    // clic adentro —abrir un desplegable, tocar una fila— hacia saltar el
    // recorrido: asi terminabas en "Confirmar e ingresar datos" mientras
    // seguias mapeando columnas.
    const isClickable = isClickableElement(targetEl);
    if (!isInput && !isClickable) return undefined;

    // El clic se registra siempre, incluso en los pasos que no auto-avanzan:
    // es la prueba de que el usuario hizo lo que el paso pedia.
    const markInteracted = () => { stepInteractedRef.current = true; setStepInteracted(true); };
    targetEl.addEventListener('click', markInteracted);

    // El avance nunca ocurre dentro del mismo tick del evento del usuario. Estos
    // escuchadores estan puestos sobre el elemento, asi que corren ANTES de que
    // React procese su propio onChange/onClick: avanzar ahi mismo cambia de paso
    // (y dispara re-renders, eventos y persistencia) mientras el formulario
    // todavia no guardo lo que el usuario acaba de elegir. En un <select> eso se
    // veia como "elegi la categoria y el campo volvio a Seleccionar...". Con el
    // salto diferido el formulario ya asento el valor, y ademas se puede
    // reconfirmar antes de dar el paso por hecho.
    const pendientes = new Set();
    const luego = (fn) => {
      const id = window.setTimeout(() => { pendientes.delete(id); fn(); }, 0);
      pendientes.add(id);
    };
    const detachMark = () => {
      targetEl.removeEventListener('click', markInteracted);
      pendientes.forEach((id) => window.clearTimeout(id));
      pendientes.clear();
    };
    if (currentStep.autoAdvance === false) return detachMark;

    const handleTargetClick = () => {
      // Dejar ejecutar el clic real del usuario y luego avanzar. Si es el
      // ultimo paso no hay "siguiente": el recorrido se cierra, con su festejo
      // y su salto al listado.
      luego(() => {
        if (stepIndex >= steps.length - 1) {
          // ...salvo que el paso declare su prueba de completado: ahi el clic no
          // alcanza, porque la accion puede fallar (un alta rechazada por el
          // backend). Se espera la prueba en el DOM; si no llega, el usuario
          // corrige y reintenta con el recorrido todavia abierto.
          if (currentStep?.doneWhen) return;
          completeRef.current?.();
        } else {
          handleNext({ autoAdvanced: true });
        }
      });
    };

    const handleTargetInput = (e) => {
      if (e.key !== 'Enter' && e.type !== 'change') return;
      luego(() => {
        // Se relee el campo (no el valor del evento): si lo elegido no quedo
        // asentado, el recorrido se queda en este paso y lo pide de nuevo, en
        // vez de saltar al siguiente dejando el campo vacio a espaldas del
        // usuario.
        const actual = document.querySelector(currentStep.selector) || targetEl;
        const val = actual && actual.value ? String(actual.value).trim() : '';
        if (val && val !== '0') {
          handleNext({ autoAdvanced: true });
        }
      });
    };

    if (isInput) {
      targetEl.addEventListener('keydown', handleTargetInput);
      targetEl.addEventListener('change', handleTargetInput);
    } else {
      targetEl.addEventListener('click', handleTargetClick);
    }

    return () => {
      detachMark();
      if (isInput) {
        targetEl.removeEventListener('keydown', handleTargetInput);
        targetEl.removeEventListener('change', handleTargetInput);
      } else {
        targetEl.removeEventListener('click', handleTargetClick);
      }
    };
  }, [currentStep?.selector, currentStep?.autoAdvance, currentStep?.doneWhen, handleNext, stepIndex, steps.length]);

  const handleComplete = useCallback(() => {
    // Ya se festejo: cualquier detector que llegue tarde no vuelve a validar ni
    // a mostrar nada. La guarda va ANTES de runValidation a proposito — revalidar
    // sobre una pantalla que ya cambio de ruta pinta un error rojo encima de un
    // recorrido terminado.
    if (tourCompletedRef.current) return;
    setValidationError(null);
    if (!runValidation()) return;
    // Recien ahora esta cerrado: si la validacion fallo, el usuario corrige y
    // reintenta con el recorrido todavia abierto.
    tourCompletedRef.current = true;

    const totalTimeMs = Date.now() - tourStartTimeRef.current;
    trackTourCompleted({
      tourId: moduleDefinition?.id || 'tour',
      totalTimeSpentMs: totalTimeMs,
      stepsCompletedCount: steps.length,
      organizationId: profile?.organization_id,
      userId: profile?.id || user?.id,
    });

    // El festejo es de la MISION, no de cada cartel que se cierra. Hay
    // recorridos que se sirven en tramos encadenados: el de bienvenida son
    // cuatro definiciones seguidas, y como cada una pasa por aca, terminar el
    // tramo de la Academia —que solo muestra donde vive el boton de cursos—
    // felicitaba al usuario por una mision que todavia no habia hecho. Un
    // tramo intermedio declara `celebrateOnComplete: false` y cierra callado;
    // el default sigue siendo festejar, asi que las guias de "Empeza a operar"
    // y las de modulo, que son una unidad completa, no cambian.
    const celebrate = moduleDefinition?.celebrateOnComplete !== false;
    const successMessage = moduleDefinition?.completeMessage
      || '🎉 ¡Felicitaciones! Misión de aprendizaje completada.';

    const cerrar = () => {
      setShowConfetti(false);
      onComplete();
      // Un recorrido puede declarar donde deja al usuario al terminar. Cerrar
      // el cartel donde estaba —una pantalla de resultados, un formulario
      // vacio— no muestra el resultado del trabajo: la guia de catalogo
      // termina en el listado de productos, con todo lo que acaba de cargar.
      if (moduleDefinition?.finishRoute) navigate(moduleDefinition.finishRoute);
    };

    if (!celebrate) {
      // Sin festejo no hay nada que mirar: esperar 1,2s dejaba un cartel muerto
      // entre tramo y tramo del recorrido de bienvenida.
      cerrar();
      return;
    }

    setShowConfetti(true);
    toast.success(successMessage);
    setTimeout(cerrar, 1200);
  }, [
    runValidation, onComplete, navigate,
    moduleDefinition?.id, moduleDefinition?.finishRoute,
    moduleDefinition?.celebrateOnComplete, moduleDefinition?.completeMessage,
    steps.length, profile?.organization_id, profile?.id, user?.id,
  ]);

  useEffect(() => {
    completeRef.current = handleComplete;
  }, [handleComplete]);

  // La guarda se libera cuando el recorrido vuelve a empezar: al cambiar de
  // guia y al retroceder a un paso que no es el ultimo ("Atras", o "Repetir"
  // desde ProgresoOnboarding, que rebobina al paso 1). Sin esto, repetir una
  // guia sin desmontar el cartel la terminaria en silencio.
  useEffect(() => {
    tourCompletedRef.current = false;
  }, [moduleDefinition?.id]);

  useEffect(() => {
    if (stepIndex < steps.length - 1) tourCompletedRef.current = false;
  }, [stepIndex, steps.length]);

  const handleSkip = useCallback(() => {
    trackTourSkipped({
      tourId: moduleDefinition?.id || 'tour',
      stepIndex,
      organizationId: profile?.organization_id,
      userId: profile?.id || user?.id,
    });
    if (typeof onSkipStep === 'function') onSkipStep();
    else handleNext();
  }, [moduleDefinition?.id, stepIndex, profile?.organization_id, profile?.id, user?.id, onSkipStep, handleNext]);

  const handleBack = useCallback(() => {
    isGoingBackRef.current = true;
    setTimeout(() => {
      isGoingBackRef.current = false;
    }, 1200);

    const prevIndex = Math.max(0, stepIndex - 1);
    const prevStep = steps[prevIndex];
    setStepIndex(prevIndex);
    setSetupTourStep(prevIndex);
    setValidationError(null);

    if (prevStep?.route) {
      navigate(prevStep.route);
    }
  }, [stepIndex, steps, navigate]);


  // El acceso a la Academia salio de los carteles de guia: aparecia en TODOS
  // los pasos de todos los recorridos y competia con la accion que el paso
  // estaba pidiendo (el usuario tenia que elegir entre seguir la guia o irse a
  // un curso). La Academia se presenta una vez, en su propio paso del recorrido
  // de bienvenida, y vive en el boton de la barra superior.

  const updateLayout = useCallback(() => {
    if (!hasSteps || !currentStep) return;

    const targetElements = currentStep.selector
      ? Array.from(document.querySelectorAll(currentStep.selector))
      : [];

    let nextTargetRect = null;
    let targetElement = targetElements[0] || null;

    if (targetElements.length === 1) {
      nextTargetRect = targetElements[0].getBoundingClientRect();
    } else if (targetElements.length > 1) {
      let minTop = Infinity;
      let minLeft = Infinity;
      let maxBottom = -Infinity;
      let maxRight = -Infinity;

      targetElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          minTop = Math.min(minTop, rect.top);
          minLeft = Math.min(minLeft, rect.left);
          maxBottom = Math.max(maxBottom, rect.bottom);
          maxRight = Math.max(maxRight, rect.right);
        }
      });

      if (minTop !== Infinity) {
        nextTargetRect = {
          top: minTop,
          left: minLeft,
          width: maxRight - minLeft,
          height: maxBottom - minTop,
          right: maxRight,
          bottom: maxBottom,
        };
      }
    }

    const normalizedRect = nextTargetRect && nextTargetRect.width > 0 && nextTargetRect.height > 0
      ? nextTargetRect
      : null;

    setTargetRect(normalizedRect);
    setFieldLabel(normalizedRect && targetElement ? getSpotlightFieldLabel(targetElement) : null);

    const tooltipSize = {
      width: tooltipRef.current?.offsetWidth || TOOLTIP_MAX_WIDTH,
      height: tooltipRef.current?.scrollHeight || 320,
    };

    if (isNavStep) {
      const margin = 16;
      const width = Math.min(360, window.innerWidth - (margin * 2));
      const height = tooltipRef.current
        ? Math.min(420, tooltipRef.current.scrollHeight + 12)
        : 340;
      const left = window.innerWidth > 640
        ? window.innerWidth - width - 24
        : (window.innerWidth - width) / 2;
      const top = window.innerWidth > 640
        ? 80
        : window.innerHeight - height - 24;

      setTooltipLayout({
        width,
        height,
        left,
        top,
        placement: 'bottom-right',
      });
      setFieldLabel(null);
    } else {
      // Un paso cuya ancla todavia no esta montada no tiene a que apuntar. Si
      // se lo deja centrado queda flotando encima del contenido, tapando
      // justamente la pantalla donde el usuario tiene que trabajar. En ese caso
      // el cartel se estaciona arriba a la derecha, a la altura del indicador
      // de etapas, y espera ahi. Los pasos que nacen sin selector (las
      // bienvenidas) siguen centrados como siempre.
      const placementForStep = normalizedRect
        ? (currentStep.placement || 'bottom')
        : (currentStep.selector ? 'parked' : 'center');

      // Un paso puede declarar que area no hay que tapar (`avoid`): en el POS,
      // el panel de cobro entero, para que el usuario vea el descuento, el
      // medio de pago y el total mientras la guia se los explica. Si no lo
      // declara, se deduce el panel flotante que contiene al objetivo.
      const declaredAvoid = currentStep.avoid && typeof document !== 'undefined'
        ? document.querySelector(currentStep.avoid)
        : null;
      const declaredAvoidRect = declaredAvoid ? declaredAvoid.getBoundingClientRect() : null;

      setTooltipLayout(computeTooltipLayout(
        normalizedRect,
        tooltipSize,
        placementForStep,
        declaredAvoidRect && declaredAvoidRect.width > 0 && declaredAvoidRect.height > 0
          ? unionRects(declaredAvoidRect, normalizedRect)
          : (normalizedRect && targetElement
            ? getPanelAvoidRect(targetElement, normalizedRect)
            : null),
      ));
    }
  }, [currentStep, hasSteps, isNavStep]);

  // Al cambiar de paso —sobre todo cuando el paso vive en OTRA pantalla y se
  // navego de ruta— el elemento objetivo puede no estar montado todavia (la
  // pagina recien carga o trae datos async). Antes el querySelector corria una
  // sola vez: si el elemento no estaba, targetRect quedaba null y el spotlight
  // caia a un overlay sin resaltado => el usuario perdia "donde hacer foco".
  // Ahora sondeamos hasta que el elemento aparezca, lo centramos con scroll y
  // recien ahi medimos; reintentamos unas pocas pasadas para que asiente.
  useEffect(() => {
    if (!hasSteps || !currentStep) return undefined;

    let cancelled = false;
    let scrolled = false;
    let settled = 0;
    let attempts = 0;
    const MAX_ATTEMPTS = 80; // ~8s a 100ms: cubre navegacion + carga async

    const tick = () => {
      if (cancelled) return;

      const el = currentStep.selector
        ? document.querySelector(currentStep.selector)
        : null;

      if (currentStep.selector && !el && !isGoingBackRef.current) {
        // Si el paso declara doneWhen, esa es la unica prueba valida: el salto
        // hacia adelante no puede saltearla solo porque la pantalla cambio.
        const nextStep = steps[stepIndex + 1];
        const advanced = currentStep.doneWhen
          ? !!document.querySelector(currentStep.doneWhen)
          : !!(nextStep && nextStep.selector && document.querySelector(nextStep.selector));
        if (advanced && autoAdvancedStepRef.current !== stepIndex) {
          autoAdvancedStepRef.current = stepIndex;
          allowBackHealRef.current = false;
          if (stepIndex >= steps.length - 1) completeRef.current?.();
          else setStepIndex((idx) => idx + 1);
          window.clearInterval(intervalId);
          return;
        }

        // Y al reves: si el paso actual no esta pero SI esta el de un paso
        // anterior, el recorrido arranco adelantado respecto de la pantalla
        // (estado viejo de una sesion que quedo a mitad). En vez de dejar el
        // cartel colgado pidiendo algo que no se ve, se retoma donde el usuario
        // realmente esta. Solo hacia el paso previo mas cercano que exista.
        for (let i = allowBackHealRef.current ? stepIndex - 1 : -1; i >= 0; i -= 1) {
          const back = steps[i];
          // Los pasos de navegacion apuntan al Topbar, que esta SIEMPRE en el
          // DOM. Si contaran como "paso anterior visible", cualquier paso cuyo
          // elemento todavia no aparecio (pantalla en otro estado, carga async)
          // devolveria el recorrido al menu y lo dejaria en loop: el usuario
          // vuelve a navegar, el selector sigue sin estar, y otra vez atras.
          if (back?.navStep) continue;
          if (back?.selector && document.querySelector(back.selector)) {
            setStepIndex(i);
            window.clearInterval(intervalId);
            return;
          }
        }
      }

      const rect = el ? el.getBoundingClientRect() : null;
      const visible = !!(rect && rect.width > 0 && rect.height > 0);

      if (visible) {
        if (!scrolled) {
          // Solo se mueve la pantalla si hace falta. El scroll suave tarda, y si
          // llega cuando el usuario ya abrio un desplegable nativo, el navegador
          // se lo cierra y pierde la opcion que estaba eligiendo. Si el campo ya
          // se ve entero, o lo esta usando, no se toca nada.
          const yaSeVe = rect.top >= 0 && rect.bottom <= window.innerHeight;
          const enUso = typeof document !== 'undefined' && document.activeElement === el;
          if (!yaSeVe && !enUso) {
            el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
          }
          scrolled = true;
        }
        updateLayout();
        settled += 1;
      } else if (attempts === 0) {
        // Primera pasada sin elemento: dejamos el cartel centrado como fallback.
        updateLayout();
      }

      attempts += 1;
      if (!currentStep.selector || settled >= 3 || attempts >= MAX_ATTEMPTS) {
        window.clearInterval(intervalId);
      }
    };

    const intervalId = window.setInterval(tick, 100);
    tick();

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [currentStep, hasSteps, stepIndex, updateLayout]);

  useLayoutEffect(() => {
    if (!hasSteps) return undefined;

    const update = () => updateLayout();
    const timeoutId = window.setTimeout(update, 180);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [hasSteps, stepIndex, updateLayout]);

  const connector = useMemo(
    () => (isNavStep ? null : getConnector(targetRect, tooltipLayout.placement)),
    [targetRect, tooltipLayout.placement, isNavStep],
  );

  const spotlightRect = useMemo(() => {
    if (!hasSteps || !targetRect || typeof window === 'undefined') return null;

    const top = Math.max(targetRect.top - HIGHLIGHT_PADDING, 0);
    const left = Math.max(targetRect.left - HIGHLIGHT_PADDING, 0);
    const right = Math.min(targetRect.right + HIGHLIGHT_PADDING, window.innerWidth);
    const bottom = Math.min(targetRect.bottom + HIGHLIGHT_PADDING, window.innerHeight);

    return {
      top,
      left,
      width: Math.max(right - left, 0),
      height: Math.max(bottom - top, 0),
      borderRadius: currentStep?.radius || 28,
    };
  }, [currentStep?.radius, hasSteps, targetRect]);

  const overlaySegments = useMemo(() => {
    if (!spotlightRect || typeof window === 'undefined') return [];

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const spotlightRight = spotlightRect.left + spotlightRect.width;
    const spotlightBottom = spotlightRect.top + spotlightRect.height;

    return [
      {
        key: 'top',
        top: 0,
        left: 0,
        width: viewportWidth,
        height: spotlightRect.top,
      },
      {
        key: 'left',
        top: spotlightRect.top,
        left: 0,
        width: spotlightRect.left,
        height: spotlightRect.height,
      },
      {
        key: 'right',
        top: spotlightRect.top,
        left: spotlightRight,
        width: Math.max(viewportWidth - spotlightRight, 0),
        height: spotlightRect.height,
      },
      {
        key: 'bottom',
        top: spotlightBottom,
        left: 0,
        width: viewportWidth,
        height: Math.max(viewportHeight - spotlightBottom, 0),
      },
    ].filter((segment) => segment.width > 0 && segment.height > 0);
  }, [spotlightRect]);

  if (!moduleDefinition) return null;

  if (!hasSteps) {
    const badgeLabel = moduleDefinition?.badge || 'Onboarding del modulo';
    const bulletsHeading = moduleDefinition?.bulletsHeading || 'Que mirar primero';

    return (
      <div className="fixed inset-0 z-[95] flex items-center justify-center px-4 py-6">
        <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]" />

        <div className="relative z-[99] w-full max-w-2xl overflow-hidden rounded-[32px] border border-white/70 bg-[color:var(--color-main-bg)] shadow-[0_30px_120px_rgba(15,23,42,0.25)]">
          <div className="bg-[color:var(--color-sidebar-topbar)] px-6 py-5 text-white">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-black tracking-tight">{moduleDefinition.title}</h2>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/75">
                  {moduleDefinition.description}
                </p>
              </div>
              <div className="shrink-0">
                <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.24em] text-white/75">
                  {badgeLabel}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-5 p-6">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5">
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-600">
                {bulletsHeading}
              </p>
              <div className="mt-4 space-y-3">
                {(moduleDefinition.bullets || []).map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-xl bg-[color:var(--color-sidebar-topbar)] text-white">
                      <ArrowRight size={14} />
                    </div>
                    <p className="text-sm font-semibold text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={onComplete}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-6 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-white transition hover:translate-y-[-1px] disabled:cursor-not-allowed disabled:opacity-70 disabled:translate-y-0"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {saving ? 'Guardando...' : introCompleteLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isLastStep = stepIndex === steps.length - 1;
  const badgeLabel = moduleDefinition?.badge || 'Recorrido guiado';

  return (
    <div className="pointer-events-none fixed inset-0 z-[95]">
      {showConfetti && (
        <div className="pointer-events-none fixed inset-0 z-[110] flex items-center justify-center overflow-hidden bg-slate-950/20 backdrop-blur-[2px] animate-in fade-in duration-300">
          <div className="relative flex flex-col items-center gap-3 rounded-[32px] border border-white/80 bg-white/95 p-8 text-center shadow-2xl animate-bounce">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 shadow-inner">
              <Sparkles size={32} className="animate-spin" />
            </div>
            <h3 className="text-xl font-black text-slate-900 tracking-tight">¡Misión de Onboarding Completada!</h3>
            <p className="text-xs font-semibold text-slate-600">¡Gran trabajo! Tu configuración ha avanzado exitosamente.</p>
          </div>
        </div>
      )}

      {spotlightRect ? (
        <>
          {/* Se removio el atenuado oscuro (los 4 segmentos): por el stacking
              context de los formularios, esas capas quedaban por encima de los
              desplegables de la app (proveedores, productos, buscadores) y los
              tapaban/atenuaban, impidiendo verlos y seleccionarlos. En un
              recorrido interactivo la guia la da el anillo resaltador (abajo),
              sin bloquear ni cubrir nada de la pantalla real. */}
          <div
            className="pointer-events-none fixed z-[96] rounded-[6px] border-2 border-[color:var(--color-accent)] ring-2 ring-[color:var(--color-accent)]/40 transition-all duration-200"
            style={spotlightRect}
          />
          <div
            className="pointer-events-none fixed z-[97] animate-ping rounded-[32px] border border-[color:var(--color-accent)]"
            style={{
              top: spotlightRect.top - 4,
              left: spotlightRect.left - 4,
              width: spotlightRect.width + 8,
              height: spotlightRect.height + 8,
              borderRadius: spotlightRect.borderRadius,
            }}
          />
          {(fieldLabel || currentStep?.title) ? (
            <div
              className="pointer-events-none fixed z-[98] max-w-[min(320px,calc(100vw-32px))] truncate rounded-full border border-[color:var(--color-accent)] bg-[color:var(--color-sidebar-topbar)] px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-white shadow-lg"
              style={{
                left: clamp(
                  spotlightRect.left,
                  VIEWPORT_MARGIN,
                  window.innerWidth - TOOLTIP_MIN_WIDTH,
                ),
                top: spotlightRect.top > 48
                  ? spotlightRect.top - 38
                  : spotlightRect.top + spotlightRect.height + 12,
              }}
            >
              {fieldLabel || currentStep?.title}
            </div>
          ) : null}
        </>
      ) : (
        // Fallback cuando el objetivo aun no se ubico (rect null): NO bloqueamos
        // ni oscurecemos la pantalla. Antes habia un overlay oscuro a pantalla
        // completa con pointer-events-auto que tapaba y bloqueaba todo (incluidos
        // los desplegables). Dejamos que el usuario interactue con la pantalla
        // real; la guia la da la tarjeta centrada.
        null
      )}

      {connector ? (
        <div
          className="pointer-events-none fixed z-[98] flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--color-accent)] bg-white text-[color:var(--color-accent)] shadow-lg"
          style={{ top: connector.top, left: connector.left }}
        >
          <connector.Icon size={18} />
        </div>
      ) : null}

      <div
        ref={tooltipRef}
        data-onboarding-prompt="true"
        className="pointer-events-auto fixed z-[99] flex w-full flex-col overflow-y-auto rounded-[26px] border border-white/70 bg-[color:var(--color-main-bg)] shadow-[0_24px_80px_rgba(15,23,42,0.24)]"
        style={{
          top: tooltipLayout.top,
          left: tooltipLayout.left,
          width: tooltipLayout.width,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: tooltipLayout.height,
        }}
      >
        <div className="bg-[color:var(--color-sidebar-topbar)] px-4 py-3.5 text-white">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-black tracking-tight">{currentStep?.title}</h2>
              <p className="mt-2 text-[13px] leading-relaxed text-white/75">
                {currentStep?.description}
              </p>
              {currentStep?.options && (
                <div className="mt-4 flex flex-col gap-2">
                  {currentStep.options.map((opt, i) => (
                    // Todas las opciones con el mismo peso visual: son caminos
                    // equivalentes y destacar uno se lee como recomendacion.
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        if (opt.action === 'switch-tour' && opt.tourId) {
                          // Cambia de guia sin navegar: la guia nueva tambien
                          // le va a pedir al usuario que llegue por el menu.
                          startSetupTour(opt.tourId);
                        } else if (opt.action === 'navigate') {
                          if (opt.route.includes('importaciones')) {
                            startSetupTour('load-catalog-excel');
                          } else if (typeof onDismiss === 'function') {
                            onDismiss();
                          }
                          navigate(opt.route);
                        } else if (opt.action === 'next' && opt.route) {
                          advanceSetupTour();
                          navigate(opt.route);
                        } else {
                          handleNext();
                        }
                      }}
                      className="w-full text-left px-4 py-3 rounded-xl border border-white bg-white text-[#0B2A36] font-black text-[11px] uppercase tracking-wider shadow-md transition-all flex items-center justify-between gap-3 hover:bg-slate-100 hover:-translate-y-0.5 active:translate-y-0"
                    >
                      <span>{opt.label}</span>
                      <ArrowRight size={14} className="text-slate-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="shrink-0 flex items-center gap-2">
              <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.24em] text-white/75">
                Paso {stepIndex + 1} de {steps.length}
              </div>
              {typeof onExit === 'function' ? (
                <button
                  type="button"
                  onClick={onExit}
                  disabled={saving}
                  title={exitLabel}
                  aria-label={exitLabel}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-rose-400/40 bg-rose-500/15 text-rose-300 transition hover:bg-rose-500 hover:text-white hover:border-rose-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <X size={14} strokeWidth={3} />
                </button>
              ) : null}
            </div>
          </div>
        </div>
        <div className="border-t border-slate-200 bg-white/90 px-4 py-3.5">
          {/* El aviso solo tiene sentido cuando el paso realmente se cierra
              tocando lo resaltado. Un paso con `hideAdvance` se cierra por otra
              via (una navegacion que el recorrido detecta), asi que el texto
              seria falso: el usuario puede tocar el elemento y ver que "no pasa
              nada" porque el avance llega despues, con la pantalla nueva. */}
          {!validationError && !currentStep?.hideAdvance && (manualNextBlocked || selfAdvancing) && (
            <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5 text-[11px] font-bold text-slate-500 flex items-center gap-2">
              <Info size={14} className="shrink-0 text-slate-400" />
              {/* Si el paso escribio su propia instruccion, esa manda: "toca lo
                  que esta resaltado" no alcanza cuando la accion es elegir un
                  archivo o mapear columnas. */}
              <span>
                {currentStep?.doneHint
                  || 'Este paso se completa en la pantalla: tocá lo que está resaltado y el recorrido sigue solo.'}
              </span>
            </div>
          )}
          {validationError && (
            <div className="mb-3 rounded-xl bg-rose-50 border border-rose-100 p-2.5 text-[11px] text-rose-600 font-bold flex items-center gap-2 animate-in fade-in slide-in-from-top-1">
              <AlertCircle size={14} className="shrink-0" />
              {validationError}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {/* En el primer paso no hay a donde volver: el boton se mostraba
                gris y ocupando lugar. Si no hay destino, no se muestra. */}
            {stepIndex === 0 ? null : (
            <button
              type="button"
              onClick={handleBack}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.16em] text-slate-600 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ArrowLeft size={16} />
              Atras
            </button>
            )}
 
            {!showAdvanceButton ? null : isLastStep ? (
              <button
                type="button"
                onClick={handleComplete}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.16em] text-white transition hover:translate-y-[-1px] disabled:cursor-not-allowed disabled:opacity-70 disabled:translate-y-0"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {saving ? 'Guardando...' : completeLabel}
              </button>
            ) : (
              !currentStep?.options ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={saving || manualNextBlocked}
                  title={manualNextBlocked ? 'Este paso se completa en la pantalla, no desde acá' : undefined}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.16em] text-white transition hover:translate-y-[-1px] disabled:cursor-not-allowed disabled:opacity-40 disabled:translate-y-0 disabled:hover:translate-y-0"
                >
                  {nextLabel}
                  <ArrowRight size={16} />
                </button>
              ) : null
            )}
          </div>

          {showSupportNote && (
            // Cartel aparte, al pie: dentro del cuerpo competia con las
            // opciones y se leia como una tercera alternativa.
            <div className="mt-3 flex justify-end">
              <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-bold text-slate-500">
                <LifeBuoy size={13} className="shrink-0 text-emerald-500" />
                <span>
                  ¿No podés hacerlo? Lo hacemos con vos.{' '}
                  {supportHelpHref ? (
                    <a
                      href={supportHelpHref}
                      target="_blank"
                      rel="noreferrer"
                      className="font-black text-emerald-600 underline underline-offset-2 hover:text-emerald-700"
                    >
                      {supportHelpLabel}
                    </a>
                  ) : (
                    <span className="font-black text-emerald-600">{supportContactName}</span>
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
